// @ts-strict-ignore
import React, { ChangeEvent, SetStateAction, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Input, Tooltip } from '@node-space/ui-components'
import { Approval } from 'constants/General'
import { stringToDecimal } from 'utils/utils'
import { ApprovalBandDraft, useApprovalRangeConfig } from './hooks/useApprovalRangeSettings'

interface ApprovalFromToRangeProps {
  rangeValues: { from: number | string; to?: number | string }
  rowIndex: number
  handleInputChange: (index: number, event: ChangeEvent<HTMLInputElement>) => void
  approvalBands: ApprovalBandDraft[]
  postLabel: string
  isApprovalRuleInvalid: boolean
  setIsApprovalRuleInvalid?: (value: SetStateAction<boolean>) => void
}

const ApprovalFromToRange = ({
  rangeValues,
  rowIndex,
  handleInputChange,
  approvalBands,
  postLabel,
  isApprovalRuleInvalid,
  setIsApprovalRuleInvalid,
}: ApprovalFromToRangeProps) => {
  const { t } = useTranslation()

  const {
    prefillFromValue,
    toInputIsDisabled,
    isUnbounded,
    currentRowToValue,
    isCurrentRowRangeInvalid,
  } = useApprovalRangeConfig(approvalBands, rowIndex)

  const currentToValueHasError = !!currentRowToValue && isCurrentRowRangeInvalid

  const onChangeToField = (rowIndex: number, e: ChangeEvent<HTMLInputElement>) => {
    approvalBands[rowIndex].to = stringToDecimal(e?.target?.value)
  }

  useEffect(() => {
    if (currentToValueHasError) {
      setIsApprovalRuleInvalid(currentToValueHasError)
    }
  }, [rowIndex, isApprovalRuleInvalid, currentToValueHasError])

  return (
    <Box flex gapX={8} width="full">
      <Input
        testId="approval-range-from-input"
        inputMode="numeric"
        label={t('from')}
        disabled
        rightSuffixText={postLabel}
        value={prefillFromValue}
      />
      <Input
        testId="approval-range-to-input"
        inputMode="numeric"
        label={t('to')}
        value={isUnbounded ? Approval.RULES.INFINITY : (rangeValues?.to?.toString() ?? '')}
        disabled={toInputIsDisabled}
        onChange={e => {
          onChangeToField(rowIndex, e)
          handleInputChange(rowIndex, e)
        }}
        rightSuffixText={postLabel}
        {...(currentToValueHasError && {
          error: true,
          Tooltip: (
            <Tooltip
              bodyContent={t('approvals.addNewRowIsDisabledError')}
              type="complex"
              position="top"
            />
          ),
        })}
      />
    </Box>
  )
}

export default ApprovalFromToRange
